import { useTranslation } from "react-i18next";

import type { ReturnTransportSummary } from "@/api.types.v2";
import type { OrderPaymentHistoryItem } from "@/api/use-order-payments";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

export function getReturnAmountDue(
  transport: ReturnTransportSummary,
  payments: OrderPaymentHistoryItem[]
): number {
  if (
    !transport.acceptedAt ||
    transport.status !== "AMOUNT_DUE" ||
    transport.payer !== "CUSTOMER" ||
    transport.amountDueCents === null ||
    transport.amountDueCents <= 0
  ) {
    return 0;
  }
  const payment = payments.find(
    (item) => item.id === transport.amountDuePaymentId
  );
  // Settlement snapshots remain unchanged after payment. Only the linked
  // pending debit establishes a currently payable obligation.
  return (payment?.status === "PENDING" || payment?.status === "FAILED") &&
    payment.transactionType === "DEBIT" &&
    payment.currency === transport.currency &&
    payment.amountCents === transport.amountDueCents
    ? payment.amountCents
    : 0;
}

function money(cents: number, currency: string, language: string): string {
  return new Intl.NumberFormat(language === "zh" ? "zh-CN" : "en-CA", {
    style: "currency",
    currency,
  }).format(cents / 100);
}

const settlementStateKeys = {
  PENDING: "pending",
  PROCESSING: "processing",
  MANUAL_PAYOUT_PENDING: "manualPayout",
  COMPLETED: "completed",
  FAILED: "failed",
} satisfies Record<ReturnTransportSummary["settlementState"], string>;

function paymentReturnTransportId(
  payment: OrderPaymentHistoryItem
): string | null {
  const value = payment.metadata?.returnTransportId;
  return typeof value === "string" ? value : null;
}

export function ReturnRefundBreakdown({
  transport,
  shipmentReference,
  payments,
  onPayAmountDue,
}: {
  transport: ReturnTransportSummary;
  shipmentReference?: string | number;
  payments: OrderPaymentHistoryItem[];
  onPayAmountDue: () => void;
}) {
  const { t, i18n } = useTranslation("app");
  if (!transport.acceptedAt || transport.status === "QUOTED") return null;
  const state = settlementStateKeys[transport.settlementState];
  const currency = transport.currency || "CAD";
  const grossRefund =
    transport.grossRefundCents ??
    transport.refundRequest?.approvedTotalCents ??
    transport.refundRequest?.totalRefundableCents ??
    0;
  const customerPaysReturn = transport.payer === "CUSTOMER";
  const customerReturnCharge = customerPaysReturn
    ? transport.totalChargeCents
    : 0;
  const netCashMovement =
    transport.netCashMovementCents ??
    Math.max(0, grossRefund - customerReturnCharge);
  const cashMovementConfirmed =
    transport.settlementState === "COMPLETED" &&
    transport.netCashMovementCents !== null;
  const amountDue = getReturnAmountDue(transport, payments);
  const relatedPayments = payments.filter(
    (payment) => paymentReturnTransportId(payment) === transport.id
  );
  const payerKey = `orderDetails.returnRefund.payer.${transport.payer}`;
  const stateKey = `orderDetails.returnRefund.state.${state}`;

  return (
    <Card className="border border-divider shadow-none">
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-4 space-y-0">
        <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
          {t("orderDetails.returnRefund.title")}
          {shipmentReference != null ? ` · SHP-${shipmentReference}` : ""}
        </span>
        <Badge
          variant={state === "failed" ? "destructive" : "secondary"}
          className={
            state === "failed"
              ? ""
              : state === "completed"
                ? "bg-success-50 text-success-700"
                : "bg-warning-50 text-warning-700"
          }
        >
          {t(stateKey)}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-small text-default-600">
          {customerPaysReturn
            ? t("orderDetails.returnRefund.separateChargeExplanation")
            : t("orderDetails.returnRefund.fullRefundExplanation", {
                payer: t(payerKey),
              })}
        </p>

        <div className="space-y-2 rounded-medium bg-default-50 p-3">
          <div className="flex justify-between gap-4 text-small">
            <span>
              {transport.refundRequest?.status === "PENDING"
                ? t("orderDetails.returnRefund.requestedRefund")
                : t("orderDetails.returnRefund.approvedRefund")}
            </span>
            <span className="font-medium text-success">
              +{money(grossRefund, currency, i18n.language)}
            </span>
          </div>
          <div className="flex justify-between gap-4 text-small">
            <span>{t("orderDetails.returnRefund.returnNet")}</span>
            <span>
              {money(transport.effectiveNetCents, currency, i18n.language)}
            </span>
          </div>
          <div className="flex justify-between gap-4 text-small">
            <span>{t("orderDetails.returnRefund.returnTax")}</span>
            <span>{money(transport.taxCents, currency, i18n.language)}</span>
          </div>
          <div className="flex justify-between gap-4 border-t border-divider pt-2 text-small">
            <span>
              {t("orderDetails.returnRefund.returnGross", {
                payer: t(payerKey),
              })}
            </span>
            <span className="font-medium">
              {money(transport.totalChargeCents, currency, i18n.language)}
            </span>
          </div>
          <div className="flex justify-between gap-4 text-small">
            <span>{t("orderDetails.returnRefund.customerReturnCharge")}</span>
            <span className="font-medium">
              {customerPaysReturn
                ? `−${money(customerReturnCharge, currency, i18n.language)}`
                : money(0, currency, i18n.language)}
            </span>
          </div>
          <div className="flex justify-between gap-4 border-t border-divider pt-2">
            <span className="font-semibold">
              {cashMovementConfirmed
                ? t("orderDetails.returnRefund.cashReturned")
                : t("orderDetails.returnRefund.estimatedCashReturned")}
            </span>
            <span className="font-semibold">
              {money(netCashMovement, currency, i18n.language)}
            </span>
          </div>
          {amountDue > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-medium bg-danger-50 p-3">
              <div>
                <p className="font-semibold text-danger">
                  {t("orderDetails.returnRefund.amountDue")}
                </p>
                <p className="text-small text-danger-700">
                  {money(amountDue, currency, i18n.language)}
                </p>
              </div>
              <Button size="sm" variant="destructive" onClick={onPayAmountDue}>
                {t("orderDetails.returnRefund.payAmountDue")}
              </Button>
            </div>
          )}
        </div>

        {relatedPayments.length > 0 && (
          <div className="space-y-2">
            <p className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("orderDetails.returnRefund.paymentHistory")}
            </p>
            {relatedPayments.map((payment) => {
              const credit = payment.transactionType === "CREDIT";
              const receipt = payment.receipts[0];
              return (
                <div
                  key={payment.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-medium border border-divider p-3 text-small"
                >
                  <div>
                    <p className="font-medium">
                      {credit
                        ? t("orderDetails.returnRefund.refundCredit")
                        : t("orderDetails.returnRefund.returnDebit")}
                    </p>
                    <p className="text-tiny text-default-500">
                      {t(
                        `orderDetails.returnRefund.paymentStatus.${payment.status}`,
                        {
                          defaultValue: payment.status.replace(/_/g, " "),
                        }
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={credit ? "text-success" : ""}>
                      {credit ? "+" : "−"}
                      {money(
                        payment.amountCents,
                        payment.currency,
                        i18n.language
                      )}
                    </p>
                    {(receipt?.fileUrl || payment.stripeReceiptUrl) && (
                      <a
                        href={
                          receipt?.fileUrl ?? payment.stripeReceiptUrl ?? "#"
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="text-tiny text-primary hover:underline"
                      >
                        {t("orderDetails.returnRefund.viewReceipt")}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
